'use client';

import {
  bulkAssignCompliancesToUsers,
  bulkDecideApplyDenyForComplianceItems,
  completeComplianceMonitorOnboarding,
  submitTaxAndAccountingOnboarding,
  TaxAndAccountingOnboardingStatus,
  useBusinessEntity,
  useGetAllComplianceItems,
  useObligationRulesGeneratorConfirmationCalendar,
  useTaxAndAccountingOnboardingResume,
} from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { CountryCode, LoadingIndicator, useToggle } from '@commenda/kakashi';
import {
  Country,
  ObligationRulesCategory,
  ObligationRulesGeneratorConfirmationDecisionType,
} from '@commenda/prisma';
import { cn } from '@commenda/utils';
import { useEffect, useMemo, useState } from 'react';
import {
  NewIncorporationLayout,
  type ProgressStepItem,
} from '../../form-builder/components/new-incorp-layout';
import { useNotification } from '../../hooks/useNotification';
import { idOfPendingDecision, resolvableRows } from './wizard/confirmationRows';
import {
  type PendingDecision,
  type PreviewRow,
} from './wizard/confirmationRows.types';
import { INTAKE_FIELDS } from './wizard/fieldOptions';
import { Part1Intake } from './wizard/Part1Intake';
import { Part2Profile } from './wizard/Part2Profile';
import { Part3CalendarPreview } from './wizard/Part3CalendarPreview';
import { Part4Assignee } from './wizard/Part4Assignee';
import { PartConfirmation } from './wizard/PartConfirmation';
import {
  clearPendingConfirmations,
  readPendingConfirmations,
  writePendingConfirmations,
} from './wizard/pendingConfirmationStorage';
import { WizardFormProvider } from './wizard/WizardFormProvider';
import {
  buildProfileAnswers,
  validateIntake,
  validateProfile,
} from './wizard/wizardLogic';
import { WizardFormData } from './wizard/wizardLogic.types';

/** The renderer serves the full onboarding flow and a trimmed confirm/deny-only flow. */
type WizardVariant = 'full' | 'confirmationOnly';

type WizardStepKey =
  | 'intake'
  | 'profile'
  | 'confirmation'
  | 'calendar'
  | 'assign';

/** Stable canonical ordering used only for persisting `lastStepIndex` (see buildDraft). */
const CANONICAL_FULL_KEYS: WizardStepKey[] = [
  'intake',
  'profile',
  'confirmation',
  'calendar',
  'assign',
];

const LEGACY_STEP_KEYS: WizardStepKey[] = [
  'intake',
  'profile',
  'calendar',
  'assign',
];

/** Namespaced key stashed in the (opaque) formData bag so resume can restore the exact step. */
const WIZARD_LAST_STEP_KEY = '__wizardLastStepKey';

/** Namespaced key under which uncommitted confirm/deny decisions ride along in the (opaque) draft formData. */
const PENDING_DECISIONS_KEY = '__pendingConfirmationDecisions';

const STEP_META: Record<
  WizardStepKey,
  { label: string; title: string; subtitle: string }
> = {
  intake: {
    label: 'Onboarding',
    title: 'Onboarding',
    subtitle: 'Review your entity and choose the fiscal year to onboard.',
  },
  profile: {
    label: 'Tax & Accounting profile',
    title: 'Tax & Accounting profile',
    subtitle: 'Tell us about your entity for this fiscal year.',
  },
  confirmation: {
    label: 'Review filings',
    title: 'Confirm your filings',
    subtitle:
      'Tell us which of these filings apply to you so your calendar reflects your facts.',
  },
  calendar: {
    label: 'Compliance calendar',
    title: 'Your compliance calendar',
    subtitle: 'Review the filings generated for this fiscal year.',
  },
  assign: {
    label: 'Assign',
    title: 'Assign work',
    subtitle: "Choose who's responsible for these filings.",
  },
};

const GENERIC_ERROR =
  'Something went wrong, please try again or contact Team Commenda if you need help.';

type OnboardingWizardRendererProps = {
  corporationId: number;
  companyId: number;
  /** Marks the workflow step complete and closes the wizard. */
  onFinish: () => Promise<void> | void;
  /** Closes the wizard without finishing (header close button). */
  onClose?: () => void;
  /**
   * `full` (default) runs the whole onboarding. `confirmationOnly` runs the trimmed
   * `[confirmation → calendar review]` flow launched from the entity banner.
   */
  variant?: WizardVariant;
  /** Fiscal year the confirmation-only flow operates on (ignored by the full flow, which derives it from intake). */
  initialFiscalYear?: number;
  /** Locks the full intake flow to a specific fiscal year (pre-fills and disables the intake FY field). */
  lockedFiscalYear?: number;
};

export const OnboardingWizardRenderer = ({
  corporationId,
  companyId,
  onFinish,
  onClose,
  variant = 'full',
  initialFiscalYear,
  lockedFiscalYear,
}: OnboardingWizardRendererProps) => {
  const { setNotification } = useNotification();
  const isStepLoading = useToggle(false);

  const { data: businessEntity, isLoading: isEntityLoading } =
    useBusinessEntity(corporationId);
  const country = businessEntity?.generalCorporationInfo
    ?.incorporationCountry as Country | undefined;
  const {
    data: complianceItemsData,
    error: complianceItemsError,
    mutate: mutateComplianceItems,
  } = useGetAllComplianceItems(undefined, { corporationId });

  const { data: resume, isLoading: isResumeLoading } =
    useTaxAndAccountingOnboardingResume(corporationId);

  const flagOn = isFeatureLaunched(
    FeatureLaunch.ObligationRulesGenerator,
    companyId
  );

  const [formData, setFormData] = useState<WizardFormData>(() => ({
    [INTAKE_FIELDS.FISCAL_YEAR]: String(
      lockedFiscalYear ?? new Date().getFullYear()
    ),
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const { errors: stillFailing } = validateProfile(formData, country);
      let changed = false;
      const next = { ...prev };
      Object.keys(prev).forEach((key) => {
        if (!stillFailing[key]) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [formData, country]);

  const [activeStepKey, setActiveStepKey] = useState<WizardStepKey>(
    variant === 'confirmationOnly' ? 'confirmation' : 'intake'
  );
  const [maxStepIndexReached, setMaxStepIndexReached] = useState(0);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [pendingDecisions, setPendingDecisions] = useState<
    Record<string, PendingDecision>
  >({});

  // Hydrate the wizard once (step + all inputs + uncommitted decisions). Full flow reads the resume
  // draft (runs after the resume read settles — on error/flag-off there's no draft → fresh start);
  // the trimmed flow reads sessionStorage.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || isResumeLoading) return;
    if (variant === 'full') {
      const draft = resume?.draft;
      if (draft) {
        const draftForm =
          lockedFiscalYear != null
            ? {
                ...(draft.formData as WizardFormData),
                [INTAKE_FIELDS.FISCAL_YEAR]: String(lockedFiscalYear),
              }
            : (draft.formData as WizardFormData);
        setFormData(draftForm);
        const savedKey = draftForm?.[WIZARD_LAST_STEP_KEY] as
          | WizardStepKey
          | undefined;
        setActiveStepKey(
          savedKey ?? LEGACY_STEP_KEYS[draft.lastStepIndex] ?? 'intake'
        );
        if (draft.assigneeUserIds) setSelectedAssignees(draft.assigneeUserIds);
        const savedPending = draftForm?.[PENDING_DECISIONS_KEY] as
          | Record<string, PendingDecision>
          | undefined;
        if (savedPending) setPendingDecisions(savedPending);
      }
    } else {
      setPendingDecisions(
        readPendingConfirmations(corporationId, initialFiscalYear ?? null)
      );
    }
    setHydrated(true);
  }, [
    hydrated,
    isResumeLoading,
    resume,
    variant,
    corporationId,
    initialFiscalYear,
    lockedFiscalYear,
  ]);

  const fiscalYear =
    variant === 'confirmationOnly'
      ? (initialFiscalYear ?? null)
      : Number(formData[INTAKE_FIELDS.FISCAL_YEAR]) || null;

  const {
    data: confirmationData,
    error: confirmationError,
    mutate: mutateConfirmation,
  } = useObligationRulesGeneratorConfirmationCalendar(
    '',
    flagOn && fiscalYear != null ? { corporationId, fiscalYear } : null
  );

  const confirmationItems = confirmationData?.calendar?.items ?? [];
  const confirmationRows = useMemo(
    () => resolvableRows(confirmationItems),
    [confirmationItems]
  );
  const hasResolvableConfirmations = flagOn && confirmationRows.length > 0;

  const [wasConfirmationStepShown, setWasConfirmationStepShown] =
    useState(false);
  useEffect(() => {
    if (hasResolvableConfirmations) setWasConfirmationStepShown(true);
  }, [hasResolvableConfirmations]);

  const includesConfirmationStep =
    hasResolvableConfirmations ||
    wasConfirmationStepShown ||
    activeStepKey === 'confirmation';

  const stepKeys = useMemo<WizardStepKey[]>(() => {
    if (variant === 'confirmationOnly') {
      return includesConfirmationStep
        ? ['confirmation', 'calendar']
        : ['calendar'];
    }
    const keys: WizardStepKey[] = ['intake', 'profile'];
    if (includesConfirmationStep) keys.push('confirmation');
    keys.push('calendar', 'assign');
    return keys;
  }, [variant, includesConfirmationStep]);

  const activeStepIndex = Math.max(0, stepKeys.indexOf(activeStepKey));
  const currentKey = stepKeys[activeStepIndex] ?? stepKeys[0];
  const isLastStep = activeStepIndex === stepKeys.length - 1;

  useEffect(() => {
    setMaxStepIndexReached((prev) => Math.max(prev, activeStepIndex));
  }, [activeStepIndex]);

  const notifyError = (message: string) =>
    setNotification({ show: true, valid: false, title: 'Error', message });

  const refreshCalendars = () =>
    Promise.all([mutateConfirmation(), mutateComplianceItems()]);

  const nextKeyAfter = (key: WizardStepKey): WizardStepKey => {
    const index = stepKeys.indexOf(key);
    return stepKeys[Math.min(stepKeys.length - 1, index + 1)];
  };

  const advanceToKey = (key: WizardStepKey) => setActiveStepKey(key);

  const buildDraft = (stepKey: WizardStepKey) => ({
    formData: {
      ...formData,
      [WIZARD_LAST_STEP_KEY]: stepKey,
      [PENDING_DECISIONS_KEY]: pendingDecisions,
    },
    lastStepIndex: Math.max(0, CANONICAL_FULL_KEYS.indexOf(stepKey)),
    assigneeUserIds: selectedAssignees,
  });

  /**
   * The intake's compliance-category selection, forwarded to the submit so the single honest
   * profile-submit regen scopes generation by it. Empty selection → undefined (generate all).
   */
  const readComplianceCategories = ():
    | ObligationRulesCategory[]
    | undefined => {
    const categories = formData[INTAKE_FIELDS.COMPLIANCE_CATEGORIES] as
      | ObligationRulesCategory[]
      | undefined;
    return categories && categories.length > 0 ? categories : undefined;
  };

  const persistProgress = async (stepKey: WizardStepKey) => {
    if (variant !== 'full' || fiscalYear == null) return;
    try {
      await submitTaxAndAccountingOnboarding({
        corporationId,
        fiscalYear,
        status: TaxAndAccountingOnboardingStatus.IN_PROGRESS,
        answers: buildProfileAnswers(formData, country),
        complianceCategories: readComplianceCategories(),
        draft: buildDraft(stepKey),
      });
    } catch {
      // Draft persistence is best-effort (e.g. flag off) — don't interrupt the wizard.
    }
  };

  useEffect(() => {
    if (hydrated) {
      if (variant === 'full') void persistProgress(currentKey);
      else
        writePendingConfirmations(corporationId, fiscalYear, pendingDecisions);
    }
  }, [pendingDecisions, hydrated, variant, corporationId, fiscalYear]);

  const entityFacts = useMemo(() => {
    const general = businessEntity?.generalCorporationInfo;
    const fyEnd = businessEntity?.accountingInfo?.fiscalYear?.data?.FIXED_DATE;
    return {
      incorporationCountry: general?.incorporationCountry,
      formationDate: general?.formationDate,
      fiscalYearEndMonth: fyEnd?.month ?? null,
      fiscalYearEndDay: fyEnd?.day ?? null,
    };
  }, [businessEntity]);

  const handleIntake = async (): Promise<boolean> => {
    const check = validateIntake(entityFacts, fiscalYear);
    if (!check.ok) {
      notifyError(check.message ?? 'Please complete this step.');
      return false;
    }
    return true;
  };

  const handleProfile = async (): Promise<boolean> => {
    const { ok, errors: profileErrors } = validateProfile(formData, country);
    if (!ok) {
      setErrors((prev) => ({ ...prev, ...profileErrors }));
      notifyError('Please answer all the required questions.');
      return false;
    }
    try {
      // submit regenerates the calendar with rule
      // applicability on, using the (optional) tax election from the profile answers and
      // the intake's compliance categories. No separate rule-applicability-off pass.
      await submitTaxAndAccountingOnboarding({
        corporationId,
        fiscalYear: fiscalYear as number,
        status: TaxAndAccountingOnboardingStatus.IN_PROGRESS,
        answers: buildProfileAnswers(formData, country),
        complianceCategories: readComplianceCategories(),
        draft: buildDraft(nextKeyAfter('profile')),
      });

      await refreshCalendars();
      return true;
    } catch (e) {
      notifyError(e instanceof Error ? e.message : GENERIC_ERROR);
      return false;
    }
  };

  const onDecide = (decisionKey: string, decision: PendingDecision) =>
    setPendingDecisions((prev) => ({ ...prev, [decisionKey]: decision }));

  const onUndo = (decisionKey: string) =>
    setPendingDecisions((prev) => {
      const next = { ...prev };
      delete next[decisionKey];
      return next;
    });

  const clearPendingDecisions = () => {
    setPendingDecisions({});
    // Full flow: the persist effect writes the emptied set into the draft on the next tick.
    if (variant !== 'full')
      clearPendingConfirmations(corporationId, fiscalYear);
  };

  const commitConfirmationDecisions = async (): Promise<boolean> => {
    const entries = Object.entries(pendingDecisions);
    if (entries.length === 0) return true; // step is skippable without deciding every filing

    const rowByKey = new Map<string, PreviewRow>(
      confirmationRows.map((row) => [idOfPendingDecision(row), row])
    );
    const decisions = entries
      .map(([key, decision]) => {
        const row = rowByKey.get(key);
        if (
          !row ||
          row.filingCompositeId === null ||
          row.effectiveYear === null
        )
          return null;
        return {
          filingCompositeId: row.filingCompositeId,
          fiscalYear: row.effectiveYear,
          decision:
            decision === 'CONFIRM'
              ? ObligationRulesGeneratorConfirmationDecisionType.CONFIRMED
              : ObligationRulesGeneratorConfirmationDecisionType.DENIED,
        };
      })
      .filter(
        (
          decision
        ): decision is {
          filingCompositeId: string;
          fiscalYear: number;
          decision: ObligationRulesGeneratorConfirmationDecisionType;
        } => decision !== null
      );

    if (decisions.length === 0) return true; // all pending were stale — treat as skippable

    let res: Response | null;
    try {
      res = await bulkDecideApplyDenyForComplianceItems(
        corporationId,
        decisions
      );
    } catch {
      notifyError(
        'We could not save your decisions. Please try again or contact Team Commenda if this keeps happening.'
      );
      return false;
    }
    await refreshCalendars();

    if (res && !res.ok) {
      notifyError(
        'We could not save your decisions. Please try again or contact Team Commenda if this keeps happening.'
      );
      return false; // retain all pending for retry
    }

    clearPendingDecisions();
    return true;
  };

  const handleFinish = async (): Promise<boolean> => {
    if (selectedAssignees.length === 0) {
      notifyError('At least one assignee is required to finish.');
      return false;
    }
    // Part 1/2 regeneration recreates compliance items with NEW ids, so a cached list
    // would 404 the bulk-assign ("ComplianceItem … found none"). Revalidate first and
    // assign over the fresh ids.
    const fresh = (await mutateComplianceItems()) as
      | { compliances?: { id: string }[] }
      | undefined;
    const complianceItemIds = (
      fresh?.compliances ??
      complianceItemsData?.compliances ??
      []
    ).map((item) => item.id);

    if (complianceItemIds.length === 0) {
      notifyError(
        'No compliance items to assign yet. Please wait a moment and try again.'
      );
      return false;
    }

    const res = await bulkAssignCompliancesToUsers({
      complianceItemIds,
      assigneeIdsToAdd: selectedAssignees,
      companyId,
    });
    if (!res?.ok) {
      notifyError(GENERIC_ERROR);
      return false;
    }
    try {
      await submitTaxAndAccountingOnboarding({
        corporationId,
        fiscalYear: fiscalYear as number,
        status: TaxAndAccountingOnboardingStatus.COMPLETED,
        answers: buildProfileAnswers(formData, country),
        complianceCategories: readComplianceCategories(),
        draft: buildDraft('assign'),
      });
    } catch (e) {
      notifyError(e instanceof Error ? e.message : GENERIC_ERROR);
      return false;
    }
    // The calendar is generated and assigned, so mark the compliance-monitor onboarding complete:
    // the Compliance Calendar page then shows this entity's calendar instead of forcing the legacy
    // onboarding/generation. Best-effort — a failure here shouldn't block finishing the wizard.
    await completeComplianceMonitorOnboarding(corporationId);
    await onFinish();
    return true;
  };

  const handleNext = async () => {
    if (isStepLoading.value) return;
    isStepLoading.on();
    try {
      if (currentKey === 'intake') {
        const intakeOk = await handleIntake();
        if (intakeOk) {
          const next = nextKeyAfter('intake');
          await persistProgress(next);
          advanceToKey(next);
        }
      } else if (currentKey === 'profile') {
        // handleProfile persists its own draft (next step) as part of attesting the profile.
        const profileOk = await handleProfile();
        if (profileOk) {
          // handleProfile may have regenerated the calendar, so decide the next step from the
          // freshly-revalidated confirmation data — this render's `stepKeys` can predate it, which
          // would otherwise skip the confirmation step straight to the calendar.
          const fresh = (await mutateConfirmation()) as typeof confirmationData;
          const showConfirmation =
            flagOn && resolvableRows(fresh?.calendar?.items ?? []).length > 0;
          advanceToKey(showConfirmation ? 'confirmation' : 'calendar');
        }
      } else if (currentKey === 'confirmation') {
        const committed = await commitConfirmationDecisions();
        if (committed) advanceToKey(nextKeyAfter('confirmation'));
      } else if (currentKey === 'calendar') {
        if (isLastStep) {
          // Trimmed (confirmation-only) flow: nothing else to submit — just close/refresh.
          await refreshCalendars();
          await onFinish();
        } else {
          const next = nextKeyAfter('calendar');
          await persistProgress(next);
          advanceToKey(next);
        }
      } else if (currentKey === 'assign') {
        await handleFinish();
      }
    } finally {
      isStepLoading.off();
    }
  };

  const onBack = () => {
    const prevKey = stepKeys[Math.max(0, activeStepIndex - 1)];
    void persistProgress(prevKey);
    setActiveStepKey(prevKey);
  };

  const onStepClick = (index: number) => {
    const key = stepKeys[index];
    if (!key) return;
    void persistProgress(key);
    setActiveStepKey(key);
  };

  const steps: ProgressStepItem[] = stepKeys.map((key, i) => ({
    id: `ta-onboarding-step-${key}`,
    text: STEP_META[key].label,
    isCompleted: i < maxStepIndexReached,
  }));

  const disableNext =
    (currentKey === 'intake' && !validateIntake(entityFacts, fiscalYear).ok) ||
    (currentKey === 'assign' && selectedAssignees.length === 0);

  const nextLabel = isLastStep
    ? 'Finish'
    : currentKey === 'confirmation'
      ? 'Continue'
      : 'Next';

  const showStep = (key: WizardStepKey) => stepKeys.includes(key);

  const confirmationLoading =
    flagOn &&
    fiscalYear != null &&
    confirmationData === undefined &&
    confirmationError === undefined;

  if (isEntityLoading || !businessEntity || isResumeLoading || !hydrated) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-secondary">
        <LoadingIndicator size={32} />
      </div>
    );
  }

  if (!flagOn) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-secondary px-6 text-center">
        <p className="txt-body-medium text-text-primary">
          Compliance onboarding isn&apos;t available for this entity yet.
        </p>
      </div>
    );
  }

  return (
    <NewIncorporationLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      onStepClick={onStepClick}
      title={STEP_META[currentKey].title}
      subtitle={STEP_META[currentKey].subtitle}
      contentClassName={'px-6'}
      header={{
        entity: businessEntity.generalCorporationInfo?.legalName
          ? {
              name: businessEntity.generalCorporationInfo.legalName,
              countryCode: (businessEntity.generalCorporationInfo
                .incorporationCountry ?? '') as CountryCode,
            }
          : undefined,
        onClose,
        hideCloseButton: !onClose,
      }}
      formActions={{
        onBack,
        onNext: () => void handleNext(),
        nextLabel,
        isLoading: isStepLoading.value,
        disableNext,
        hideBack: activeStepIndex === 0,
      }}
      isWideUi={false}
    >
      <WizardFormProvider
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        corporationId={corporationId}
        companyId={companyId}
      >
        {showStep('intake') && (
          <div className={cn(currentKey !== 'intake' && 'hidden')}>
            <Part1Intake
              businessEntity={businessEntity}
              lockedFiscalYear={lockedFiscalYear}
            />
          </div>
        )}
        {showStep('profile') && (
          <div className={cn(currentKey !== 'profile' && 'hidden')}>
            {fiscalYear != null && (
              <Part2Profile
                corporationId={corporationId}
                fiscalYear={fiscalYear}
                country={country}
              />
            )}
          </div>
        )}
        {showStep('confirmation') && (
          <div className={cn(currentKey !== 'confirmation' && 'hidden')}>
            <PartConfirmation
              rows={confirmationRows}
              pending={pendingDecisions}
              isLoading={confirmationLoading}
              isCommitting={isStepLoading.value}
              country={
                businessEntity.generalCorporationInfo?.incorporationCountry
              }
              onDecide={onDecide}
              onUndo={onUndo}
            />
          </div>
        )}
        {showStep('calendar') && (
          <div className={cn(currentKey !== 'calendar' && 'hidden')}>
            {fiscalYear != null && (
              <Part3CalendarPreview
                flagOn={flagOn}
                confirmationData={confirmationData}
                confirmationError={confirmationError}
                mutateConfirmation={mutateConfirmation}
                plainData={complianceItemsData}
                plainError={complianceItemsError}
                mutatePlain={mutateComplianceItems}
              />
            )}
          </div>
        )}
        {showStep('assign') && (
          <div className={cn(currentKey !== 'assign' && 'hidden')}>
            <Part4Assignee
              companyId={companyId}
              selectedMemberId={selectedAssignees[0] ?? ''}
              onSelectMembers={setSelectedAssignees}
            />
          </div>
        )}
      </WizardFormProvider>
    </NewIncorporationLayout>
  );
};
